import { useEffect } from 'react';
import { useRouteError, isRouteErrorResponse, Link } from 'react-router-dom';

import { ROUTES } from '@/app/router/route-constants';
import { Button } from '@/shared/components/button';
import { isBrowserExtensionDomError } from '@/shared/components/error-boundary';
import { useDocumentTitle } from '@/shared/hooks/use-document-title';

export function ErrorPage() {
  useDocumentTitle('Error');
  const error = useRouteError();

  // Google Translate / extension DOM mutations should not trap users on this page.
  useEffect(() => {
    if (isBrowserExtensionDomError(error)) {
      window.location.reload();
    }
  }, [error]);

  if (isBrowserExtensionDomError(error)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-content-bg px-4 text-center text-sm text-secondary">
        Recovering…
      </div>
    );
  }

  let message = 'An unexpected error occurred.';
  if (isRouteErrorResponse(error)) {
    message = error.statusText || message;
  } else if (error instanceof Error) {
    message = error.message;
  }

  // React.lazy / dynamic imports can fail when the browser is holding
  // stale build assets (e.g. after a redeploy while the tab stayed open).
  // In that case we want to show an actionable message rather than the
  // raw technical error (e.g. disallowed MIME type / chunk load failure).
  if (error instanceof Error) {
    const raw = error.message;
    const isChunkLoadFailure =
      raw.includes('error loading dynamically imported module') ||
      raw.includes('ChunkLoadError') ||
      raw.includes('disallowed MIME type');

    if (isChunkLoadFailure) {
      message =
        'You were inactive for a while. Please reload the page to continue.';
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-content-bg px-4 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-danger/15">
        <svg
          className="h-8 w-8 text-danger"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01"
          />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-body">Something went wrong</h1>
      <p className="mt-2 max-w-md text-sm text-secondary">{message}</p>
      <div className="mt-6 flex gap-3">
        <Button
          variant="outline"
          onClick={() => {
            window.location.reload();
          }}
        >
          Reload
        </Button>
        <Link to={ROUTES.DASHBOARD}>
          <Button>Back to Dashboard</Button>
        </Link>
      </div>
    </div>
  );
}
